import React, { useState } from "react";
import { Modal, Stack, Group, Text, Button, Box, Divider } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconUserCheck } from "@tabler/icons-react";
import ClientLookup from "../common/ClientLookup";
import { useMessaging } from "../../context/MessagingContext";
import type { Conversation } from "../../types/messaging";
import styles from "./AssignClientModal.module.css";

interface AssignClientModalProps {
  opened: boolean;
  onClose: () => void;
  conversation: Conversation;
}

/**
 * Link (or reassign) a client to a conversation. Never auto-links: when the
 * phone matches a client the backend surfaces it as a "potential match" with a
 * one-tap Link; otherwise the staffer searches (ClientLookup also supports
 * creating a new client from scratch). Reused for both "Assign" (unlinked) and
 * "Reassign" (already linked) — assigning just replaces the link.
 */
export const AssignClientModal: React.FC<AssignClientModalProps> = ({
  opened,
  onClose,
  conversation,
}) => {
  const { assignClient } = useMessaging();
  const [selectedClientId, setSelectedClientId] = useState("");
  const [linking, setLinking] = useState(false);

  const potentialMatch = conversation.potential_match;

  const link = async (clientUuid: string) => {
    if (!clientUuid) return;
    setLinking(true);
    try {
      await assignClient(conversation.conversation_id, clientUuid);
      setSelectedClientId("");
      onClose();
    } catch {
      // Leave the modal open so the user can retry, and surface the failure.
      notifications.show({
        title: "Couldn't link client",
        message: "Something went wrong. Please try again.",
        color: "var(--color-danger)",
      });
    } finally {
      setLinking(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={conversation.client ? "Reassign client" : "Assign to client"}
      centered
    >
      <Stack gap="md">
        {potentialMatch && (
          <>
            <Box className={styles.matchCard}>
              <Group justify="space-between" wrap="nowrap" gap="sm">
                <Group gap="xs" wrap="nowrap">
                  <IconUserCheck size={20} color="var(--brand-teal)" />
                  <Box>
                    <Text size="xs" c="dimmed">
                      Potential match
                    </Text>
                    <Text fw={600}>
                      {potentialMatch.name || "Unnamed client"}
                    </Text>
                  </Box>
                </Group>
                <Button
                  size="xs"
                  color="teal"
                  loading={linking}
                  onClick={() => link(potentialMatch.uuid)}
                >
                  Link to client
                </Button>
              </Group>
            </Box>
            <Divider label="or search" labelPosition="center" />
          </>
        )}

        <ClientLookup
          value={selectedClientId}
          onChange={setSelectedClientId}
          error={undefined}
        />

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="teal"
            loading={linking}
            disabled={!selectedClientId}
            onClick={() => link(selectedClientId)}
          >
            Link
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default AssignClientModal;
